import { scrollToUserMessageTriggerAtom } from '@/atoms/chatAtoms';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionConversation } from '../types/types';

interface UseAutoScrollProps {
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  conversation: SessionConversation;
  /** Ref to the latest conversation group - used for scroll-to-top on follow-up queries */
  latestGroupRef?: React.RefObject<HTMLDivElement>;
  /** Whether content is actively streaming in (used for scroll indicator visibility) */
  isStreaming?: boolean;
}

interface UseAutoScrollReturn {
  hasMoreContentBelow: boolean;
  scrollToBottom: () => void;
}

const SCROLL_THRESHOLD = 50;
/** Extra scroll padding to clear the bottom gradient overlay (h-24 = 96px) in ChatTimeline */
const BOTTOM_GRADIENT_HEIGHT = 96;

/**
 * Hook to track scroll position and provide scroll-to-bottom functionality.
 * Also handles scrolling to the top when a new follow-up query is sent.
 */
export const useAutoScroll = ({
  scrollAreaRef,
  conversation,
  latestGroupRef,
  isStreaming = false,
}: UseAutoScrollProps): UseAutoScrollReturn => {
  const [hasContentBelow, setHasContentBelow] = useState(false);
  const [hasReachedBottomSinceQuery, setHasReachedBottomSinceQuery] = useState(true);

  // Follow mode: auto-scroll to bottom as content streams in.
  // Activated when user clicks scroll-to-bottom during streaming.
  // Deactivated when user scrolls up or when streaming stops.
  const isFollowingRef = useRef(false);

  // External trigger for scroll (from pending messages)
  const scrollTrigger = useAtomValue(scrollToUserMessageTriggerAtom);

  // Track user message count to detect new follow-up queries
  const userMessageCount = conversation.timeline.filter(
    (entry) => entry.type === 'user_message'
  ).length;
  const prevUserMessageCountRef = useRef(userMessageCount);

  const getViewport = useCallback(() => {
    return scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]'
    ) as HTMLElement | null;
  }, [scrollAreaRef]);

  const checkScrollPosition = useCallback(() => {
    const viewport = getViewport();
    if (!viewport) {
      setHasContentBelow(false);
      return;
    }

    const { scrollHeight, scrollTop, clientHeight } = viewport;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + SCROLL_THRESHOLD;
    const hasOverflow = scrollHeight > clientHeight;
    
    setHasContentBelow(!isAtBottom && hasOverflow);
    
    if (isAtBottom) {
      setHasReachedBottomSinceQuery(true);
    }
  }, [getViewport]);

  const performScroll = useCallback((viewport: HTMLElement, behavior: ScrollBehavior = 'smooth') => {
    // Scroll to just above the last references section (if present),
    // so the user lands at the end of the answer, not the bibliography.
    // Adds padding to clear the bottom gradient overlay in ChatTimeline.
    const referencesSections = viewport.querySelectorAll('[data-references-section]');
    const lastReferences = referencesSections[referencesSections.length - 1] as HTMLElement | undefined;

    if (lastReferences) {
      const viewportTop = viewport.getBoundingClientRect().top;
      const refTop = lastReferences.getBoundingClientRect().top;
      const scrollTarget = viewport.scrollTop + (refTop - viewportTop) - viewport.clientHeight + BOTTOM_GRADIENT_HEIGHT;

      viewport.scrollTo({
        top: Math.max(0, scrollTarget),
        behavior,
      });
    } else {
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior,
      });
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    const viewport = getViewport();
    if (viewport) {
      // Immediately hide indicator when scrolling to bottom
      setHasContentBelow(false);
      setHasReachedBottomSinceQuery(true);

      // Enter follow mode if content is still streaming
      if (isStreaming) {
        isFollowingRef.current = true;
      }

      performScroll(viewport);
    }
  }, [getViewport, isStreaming, performScroll]);

  // Check scroll position on timeline changes + follow mode auto-scroll
  useEffect(() => {
    // If in follow mode and still streaming, auto-scroll to keep up with new content
    if (isFollowingRef.current && isStreaming) {
      const viewport = getViewport();
      if (viewport) {
        const timer = setTimeout(() => performScroll(viewport, 'instant'), 50);
        return () => clearTimeout(timer);
      }
    }

    // Otherwise, just check scroll position
    const timer = setTimeout(checkScrollPosition, 50);
    return () => clearTimeout(timer);
  }, [conversation.timeline.length, conversation.streamingState, checkScrollPosition, isStreaming, getViewport, performScroll]);

  // Exit follow mode when streaming stops
  useEffect(() => {
    if (!isStreaming) {
      isFollowingRef.current = false;
    }
  }, [isStreaming]);

  // Add scroll event listener to track user scrolling
  useEffect(() => {
    const viewport = getViewport();
    if (!viewport) return;

    let lastScrollTop = viewport.scrollTop;

    const handleScroll = () => {
      // Detect user scrolling upward to exit follow mode.
      // Programmatic scrolls always go downward (toward bottom), so an upward
      // scroll reliably indicates user intent to disengage.
      if (isFollowingRef.current && viewport.scrollTop < lastScrollTop) {
        isFollowingRef.current = false;
      }
      lastScrollTop = viewport.scrollTop;

      checkScrollPosition();
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [getViewport, checkScrollPosition]);

  /**
   * Retry-scroll helper: attempts to find the latest [data-user-message] element
   * and scroll it into view, retrying up to `maxAttempts` times if the viewport
   * or element isn't ready yet. Returns a cleanup function that cancels all
   * pending timeouts/rAFs.
   */
  const retryScrollToUserMessage = useCallback(
    (options?: {
      maxAttempts?: number;
      initialDelay?: number;
      fallbackRef?: React.RefObject<HTMLDivElement>;
      alreadyPresentUserMessages?: ReadonlySet<Element>;
    }): (() => void) => {
      const {
        maxAttempts = 5,
        initialDelay = 0,
        fallbackRef,
        alreadyPresentUserMessages,
      } = options ?? {};
      const retryDelay = 100;
      let attempts = 0;
      let cancelled = false;
      const pendingTimers: ReturnType<typeof setTimeout>[] = [];
      const pendingRafs: number[] = [];

      const scrollElementIntoView = (element: Element) => {
        pendingRafs.push(
          requestAnimationFrame(() => {
            if (!cancelled) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          })
        );
      };

      const attemptScroll = () => {
        if (cancelled) return;

        const viewport = getViewport();
        const userMessages = viewport?.querySelectorAll('[data-user-message="true"]');
        const lastUserMessage = userMessages?.[userMessages.length - 1];
        const scrollTarget = alreadyPresentUserMessages
          ? Array.from(userMessages ?? [])
              .reverse()
              .find((message) => !alreadyPresentUserMessages.has(message))
          : lastUserMessage;

        if (scrollTarget) {
          scrollElementIntoView(scrollTarget);
        } else if (attempts < maxAttempts) {
          attempts++;
          pendingTimers.push(setTimeout(attemptScroll, retryDelay));
        } else {
          const fallbackTarget = lastUserMessage ?? fallbackRef?.current;
          if (fallbackTarget) {
            scrollElementIntoView(fallbackTarget);
          }
        }
      };

      if (initialDelay > 0) {
        pendingTimers.push(setTimeout(attemptScroll, initialDelay));
      } else {
        attemptScroll();
      }

      return () => {
        cancelled = true;
        pendingTimers.forEach(clearTimeout);
        pendingRafs.forEach(cancelAnimationFrame);
      };
    },
    [getViewport]
  );

  // Scroll to show the latest user message when messages are sent
  // Handles both new chats (first message) and follow-ups (subsequent messages)
  useEffect(() => {
    const prevCount = prevUserMessageCountRef.current;

    // Detect new chat: count went from higher to lower (conversation cleared/new chat)
    // Reset ref to sync with new conversation state
    if (userMessageCount < prevCount) {
      prevUserMessageCountRef.current = userMessageCount;
      setHasReachedBottomSinceQuery(true);
      if (userMessageCount >= 1) {
        return retryScrollToUserMessage({
          initialDelay: 150,
          fallbackRef: latestGroupRef,
        });
      }
      return;
    }

    // New message sent (count increased) - scroll to latest user message
    // Reset the "reached bottom" flag so the indicator can show for new content
    if (userMessageCount > prevCount) {
      setHasReachedBottomSinceQuery(false);
      prevUserMessageCountRef.current = userMessageCount;
      return retryScrollToUserMessage({
        initialDelay: 150,
        fallbackRef: latestGroupRef,
      });
    }

    prevUserMessageCountRef.current = userMessageCount;
  }, [userMessageCount, latestGroupRef, retryScrollToUserMessage]);

  // Watch for external scroll trigger (from pending messages in ChatInput)
  // This provides a direct scroll trigger that doesn't depend on userMessageCount timing
  const prevScrollTriggerRef = useRef(scrollTrigger);
  const userMessagesAtPreviousCommitRef = useRef<Set<Element>>(new Set());
  useEffect(() => {
    if (scrollTrigger > prevScrollTriggerRef.current) {
      prevScrollTriggerRef.current = scrollTrigger;
      return retryScrollToUserMessage({
        maxAttempts: 10,
        fallbackRef: latestGroupRef,
        alreadyPresentUserMessages: new Set(userMessagesAtPreviousCommitRef.current),
      });
    }
  }, [scrollTrigger, latestGroupRef, retryScrollToUserMessage]);

  // Keep the previous commit's nodes so a message committed with the trigger
  // is eligible immediately, while older messages remain retry-only.
  useEffect(() => {
    const viewport = getViewport();
    userMessagesAtPreviousCommitRef.current = new Set(
      viewport?.querySelectorAll('[data-user-message="true"]') ?? []
    );
  });

  // Compute final visibility: show indicator when there's content below AND
  // (content is streaming OR user hasn't reached bottom since last query)
  // This prevents the indicator from showing when user scrolls back up after streaming ends
  const hasMoreContentBelow = hasContentBelow && (isStreaming || !hasReachedBottomSinceQuery);

  return { hasMoreContentBelow, scrollToBottom };
};
